import { useEffect, useRef } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useLocation,
  HeadContent,
} from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import "../styles.css";

function NotFoundComponent() {
  const location = useLocation();
  const router = useRouter();
  const { t } = useTranslation("common");

  useEffect(() => {
    const parts = location.pathname.split("/").filter(Boolean);
    const firstSegment = parts[0];
    if (firstSegment !== "en" && firstSegment !== "pt") {
      let lang = "en";
      if (typeof window !== "undefined") {
        const savedLang = localStorage.getItem("bizu-lang");
        if (savedLang) {
          lang = savedLang;
        } else {
          const browserLanguage = navigator.language;
          if (browserLanguage && browserLanguage.startsWith("pt")) {
            lang = "pt";
          }
          localStorage.setItem("bizu-lang", lang);
        }
      }
      router.navigate({
        to: `/${lang}${location.pathname === "/" ? "" : location.pathname}` as any,
        replace: true,
      });
    }
  }, [location.pathname, router]);

  const parts = location.pathname.split("/").filter(Boolean);
  const firstSegment = parts[0];
  const isLanguagePath = firstSegment === "en" || firstSegment === "pt";

  if (!isLanguagePath) {
    return null; // redirecting, don't flash 404
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">
          {t("errors.notFoundTitle", "Page not found")}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("errors.notFoundDesc", "The page you're looking for doesn't exist or has been moved.")}
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {t("errors.goHome", "Go home")}
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  const { t } = useTranslation("common");

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          {t("errors.errorTitle", "This page didn't load")}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("errors.errorDesc", "Something went wrong on our end. You can try refreshing or head back home.")}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {t("errors.tryAgain", "Try again")}
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            {t("errors.goHome", "Go home")}
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const location = useLocation();
  const prevPathnameRef = useRef(location.pathname);

  useEffect(() => {
    const getSuffix = (path: string) => {
      const parts = path.split("/").filter(Boolean);
      return parts.slice(1).join("/");
    };

    const prevSuffix = getSuffix(prevPathnameRef.current);
    const currSuffix = getSuffix(location.pathname);

    // Rola a tela para o topo instantaneamente apenas se a rota/página realmente mudou
    if (!location.hash && prevSuffix !== currSuffix) {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }

    prevPathnameRef.current = location.pathname;
  }, [location.pathname, location.hash]);

  return (
    <QueryClientProvider client={queryClient}>
      <HeadContent />
      <Outlet />
    </QueryClientProvider>
  );
}
